import type {
  PublicRsvpMeta,
  RsvpGuest,
  RsvpGuestCreate,
  RsvpGuestUpdate,
  WalkInGuest,
  WalkInGuestCreate,
  WalkInGuestUpdate,
} from "../types/rsvp";
import { rsvpApi } from "./rsvp";

/** Walk-in guests share the same rsvp_guests table/routes as RSVP + Guest List (channel="walk_in"). */
function toRsvpGuestCreate(payload: WalkInGuestCreate): RsvpGuestCreate {
  return {
    name: payload.name,
    phone: payload.phone,
    email: payload.email ?? null,
    party_size: payload.party_size,
    category: "stag",
    tier_counts: payload.tier_counts,
    amount_paid: payload.amount ?? null,
    notes: payload.notes ?? null,
    whatsapp_opt_in: payload.whatsapp_opt_in ?? false,
  };
}

function toRsvpGuestUpdate(payload: WalkInGuestUpdate): RsvpGuestUpdate {
  const update: RsvpGuestUpdate = {};
  if (payload.name !== undefined) update.name = payload.name;
  if (payload.phone !== undefined) update.phone = payload.phone;
  if (payload.party_size !== undefined) update.party_size = payload.party_size;
  if (payload.tier_counts !== undefined) update.tier_counts = payload.tier_counts;
  if (payload.email !== undefined) update.email = payload.email;
  if (payload.amount !== undefined) update.amount_paid = payload.amount;
  if (payload.notes !== undefined) update.notes = payload.notes;
  if (payload.whatsapp_opt_in !== undefined) update.whatsapp_opt_in = payload.whatsapp_opt_in;
  return update;
}

function toWalkInGuest(guest: RsvpGuest): WalkInGuest {
  return {
    id: guest.id,
    event_id: guest.event_id,
    name: guest.name,
    phone: guest.phone,
    email: guest.email ?? null,
    party_size: guest.party_size,
    tier_counts: guest.tier_counts ?? {},
    amount: guest.amount ?? guest.amount_paid ?? null,
    notes: guest.notes ?? null,
    whatsapp_opt_in: guest.whatsapp_opt_in ?? false,
    created_at: guest.created_at,
    updated_at: guest.updated_at ?? null,
  };
}

export const walkInApi = {
  list: async (eventId: string, token?: string): Promise<WalkInGuest[]> => {
    const guests = await rsvpApi.listGuests(eventId, token);
    return guests.filter((g) => g.channel === "walk_in").map(toWalkInGuest);
  },

  create: async (eventId: string, payload: WalkInGuestCreate, token?: string): Promise<WalkInGuest> => {
    const guest = await rsvpApi.createGuest(
      eventId,
      { ...toRsvpGuestCreate(payload), source: "walk_in", channel: "walk_in" },
      token,
    );
    return toWalkInGuest(guest);
  },

  update: async (
    eventId: string,
    guestId: string,
    payload: WalkInGuestUpdate,
    token?: string,
  ): Promise<WalkInGuest> => {
    const guest = await rsvpApi.updateGuest(eventId, guestId, toRsvpGuestUpdate(payload), token);
    return toWalkInGuest(guest);
  },

  delete: (eventId: string, guestId: string, token?: string) => rsvpApi.deleteGuest(eventId, guestId, token),

  importCsv: async (
    eventId: string,
    rows: WalkInGuestCreate[],
    token?: string,
  ): Promise<{ created: number; skipped: number; errors: string[] }> => {
    let created = 0;
    let skipped = 0;
    const errors: string[] = [];
    for (let i = 0; i < rows.length; i += 1) {
      try {
        await walkInApi.create(eventId, rows[i], token);
        created += 1;
      } catch (err) {
        skipped += 1;
        errors.push(`Row ${i + 1}: ${err instanceof Error ? err.message : "Failed"}`);
      }
    }
    return { created, skipped, errors };
  },

  publicMeta: (inviteToken: string): Promise<PublicRsvpMeta> => rsvpApi.publicMeta(inviteToken),

  publicSubmit: async (inviteToken: string, payload: WalkInGuestCreate): Promise<WalkInGuest> => {
    const guest = await rsvpApi.publicSubmit(inviteToken, toRsvpGuestCreate(payload));
    return toWalkInGuest(guest);
  },
};
